import ky from "ky";
import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrl, buildRestUrlWithQuery, buildAuthHeadersForKy } from "./RestService";
import IUserModel from "../models/IUserModel";
import ISupplierModel from "../models/ISupplierModel";
import IUnanetLegalEntity from "../models/Unanet/IUnanetLegalEntity";
import IUnanetOrganization from "../models/Unanet/IUnanetOrganization";
import IUnanetAccount from "../models/Unanet/IUnanetAccount";
import IUnanetProjectSummary from "../models/Unanet/IUnanetProjectSummary";
import IUnanetExpenseType from "../models/Unanet/IUnanetExpenseType";
import IUnanetProjectTask from "../models/Unanet/IUnanetProjectTask";
import { IUnanetLegalEntityParams, IUnanetOrganizationParams, IUnanetAccountParams } from "../models/Unanet/IUnanetQueryParams";
import IVendorModel from "@models/IVendorModel";
import IUnanetAccountMappingsResponse from "../models/Unanet/IUnanetAccountMappingsResponse";
import IUnanetAccountMappingUpsertBody from "../models/Unanet/IUnanetAccountMappingUpsertBody";
import {
    IUnanetActivityLogParams,
    IUnanetActivityLogResponse,
    IUnanetDashboardStatsResponse,
    IUnanetHealthResponse,
    IUnanetOrderSyncStatusResponse,
    IUnanetQueueItemDetailResponse,
    IUnanetResolveQueueItemBody,
    IUnanetResolveResponse,
    IUnanetRetryResponse,
    IUnanetSyncLogDetailResponse,
} from "../models/Unanet/IUnanetDashboardQueryParams";

interface TestConnectionBody {
    unanet_base_url: string;
    unanet_customer_id?: string;
    unanet_username?: string;
    unanet_password?: string;
}

interface TestConnectionResponse {
    success: boolean;
    error?: string;
    user?: { firstName: string; lastName: string; organization?: { name: string } };
}

const testConnection = async (
    body: TestConnectionBody,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<TestConnectionResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetTestConnection });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.post(url, { json: body, headers });
    return (await res.json()) as TestConnectionResponse;
};

const searchLegalEntities = async (
    params: IUnanetLegalEntityParams,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<IUnanetLegalEntity[]> => {
    const url = buildRestUrlWithQuery({ endpoint: RestEndpointsEnum.UnanetGetLegalEntities, query: params });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.get(url, { headers });
    const data = (await res.json()) as { legalEntities: IUnanetLegalEntity[] };
    return data.legalEntities || [];
};

const getOrganizations = async (
    params: IUnanetOrganizationParams,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<IUnanetOrganization[]> => {
    const url = buildRestUrlWithQuery({ endpoint: RestEndpointsEnum.UnanetGetOrganizations, query: params });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.get(url, { headers });
    const data = (await res.json()) as { organizations: IUnanetOrganization[] };
    return data.organizations || [];
};

const searchAccounts = async (
    params: IUnanetAccountParams,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<IUnanetAccount[]> => {
    const url = buildRestUrlWithQuery({ endpoint: RestEndpointsEnum.UnanetGetAccounts, query: params });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.get(url, { headers });
    const data = (await res.json()) as { accounts: IUnanetAccount[] };
    return data.accounts || [];
};

const searchProjects = async (
    params: { search: string },
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<IUnanetProjectSummary[]> => {
    const url = buildRestUrlWithQuery({ endpoint: RestEndpointsEnum.UnanetSearchProjects, query: params });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.get(url, { headers });
    const data = (await res.json()) as { projects: IUnanetProjectSummary[] };
    return data.projects || [];
};

const getAccountMappings = async (
    catalogGuid: string,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<IUnanetAccountMappingsResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetGetAccountMappings, params: { catalogGuid } });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.get(url, { headers });
    return (await res.json()) as IUnanetAccountMappingsResponse;
};

const upsertAccountMapping = async (
    categoryGuid: string,
    body: IUnanetAccountMappingUpsertBody,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<{ mapping: Record<string, unknown> }> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetUpsertAccountMapping, params: { categoryGuid } });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.put(url, { json: body, headers });
    return (await res.json()) as { mapping: Record<string, unknown> };
};

const deleteAccountMapping = async (
    categoryGuid: string,
    catalogGuid: string,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<{ success: boolean }> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetDeleteAccountMapping, params: { categoryGuid } });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.delete(url, { json: { catalogGuid }, headers });
    return (await res.json()) as { success: boolean };
};

const getExpenseTypes = async (userModel: IUserModel, supplierModel: ISupplierModel): Promise<IUnanetExpenseType[]> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetGetExpenseTypes });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.get(url, { headers });
    const data = (await res.json()) as { expenseTypes: IUnanetExpenseType[] };
    return data.expenseTypes || [];
};

const updateVendorMapping = async (
    vendorGuid: string,
    mapping: {
        unanet_project_key: number | null;
        unanet_project_code: string | null;
        unanet_project_title: string | null;
        unanet_project_type_key?: number | null;
        unanet_project_type_name?: string | null;
        unanet_requires_tasks?: boolean;
    },
    userModel: IUserModel,
    supplierModel: ISupplierModel,
    vendorModel: IVendorModel
): Promise<void> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetUpdateVendorProject, params: { vendorGuid } });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel, vendorModel });
    await ky.put(url, { json: mapping, headers });
};

const updateVendorExpenseType = async (
    vendorGuid: string,
    body: { unanet_expense_type_key: number | null; unanet_expense_type_name: string | null },
    userModel: IUserModel,
    supplierModel: ISupplierModel,
    vendorModel: IVendorModel
): Promise<void> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetUpdateVendorExpenseType, params: { vendorGuid } });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel, vendorModel });
    await ky.put(url, { json: body, headers });
};

const getHealth = async (userModel: IUserModel, supplierModel: ISupplierModel): Promise<IUnanetHealthResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetHealth });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.get(url, { headers });
    return (await res.json()) as IUnanetHealthResponse;
};

const getDashboardStats = async (userModel: IUserModel, supplierModel: ISupplierModel): Promise<IUnanetDashboardStatsResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetDashboardStats });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.get(url, { headers });
    return (await res.json()) as IUnanetDashboardStatsResponse;
};

const getActivityLog = async (
    params: IUnanetActivityLogParams,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<IUnanetActivityLogResponse> => {
    const url = buildRestUrlWithQuery({ endpoint: RestEndpointsEnum.UnanetDashboardActivityLog, query: params });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.get(url, { headers });
    return (await res.json()) as IUnanetActivityLogResponse;
};

const getQueueItemDetail = async (
    queueGuid: string,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<IUnanetQueueItemDetailResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetGetQueueItemDetail, params: { queueGuid } });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.get(url, { headers });
    return (await res.json()) as IUnanetQueueItemDetailResponse;
};

const retryQueueItem = async (queueGuid: string, userModel: IUserModel, supplierModel: ISupplierModel): Promise<IUnanetRetryResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetRetryQueueItem, params: { queueGuid } });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.post(url, { headers });
    return (await res.json()) as IUnanetRetryResponse;
};

const resolveQueueItem = async (
    queueGuid: string,
    body: IUnanetResolveQueueItemBody,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<IUnanetResolveResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetResolveQueueItem, params: { queueGuid } });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.put(url, { json: body, headers });
    return (await res.json()) as IUnanetResolveResponse;
};

const getOrderSyncStatus = async (
    orderGuid: string,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<IUnanetOrderSyncStatusResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetOrderSyncStatus, params: { orderGuid } });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.get(url, { headers });
    return (await res.json()) as IUnanetOrderSyncStatusResponse;
};

const queueJournalEntry = async (
    body: { orderGuid: string },
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<{ success: boolean; queueEntry: { guid: string } }> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetQueueJournalEntry });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.post(url, { json: body, headers });
    return (await res.json()) as { success: boolean; queueEntry: { guid: string } };
};

const processQueue = async (userModel: IUserModel, supplierModel: ISupplierModel): Promise<{ success: boolean }> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetProcessQueue });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.post(url, { headers });
    return (await res.json()) as { success: boolean };
};

const getSyncLogDetail = async (
    syncLogGuid: string,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<IUnanetSyncLogDetailResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetGetSyncLogDetail, params: { syncLogGuid } });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.get(url, { headers });
    return (await res.json()) as IUnanetSyncLogDetailResponse;
};

const getProjectTasks = async (projectKey: number, userModel: IUserModel, supplierModel: ISupplierModel): Promise<IUnanetProjectTask[]> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetGetProjectTasks, params: { projectKey: String(projectKey) } });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.get(url, { headers });
    const data = (await res.json()) as { tasks: IUnanetProjectTask[] };
    return data.tasks || [];
};

const getVendorTaskMappings = async (
    vendorGuid: string,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<{ taskMappings: IVendorTaskMappingResponse[] }> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetGetVendorTaskMappings, params: { vendorGuid } });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.get(url, { headers });
    return (await res.json()) as { taskMappings: IVendorTaskMappingResponse[] };
};

const upsertVendorTaskMapping = async (
    vendorGuid: string,
    body: { categoryGuid: string; unanet_task_key: number; unanet_task_name: string },
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<{ success: boolean }> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetUpsertVendorTaskMapping, params: { vendorGuid } });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.put(url, { json: body, headers });
    return (await res.json()) as { success: boolean };
};

const deleteVendorTaskMapping = async (
    vendorGuid: string,
    categoryGuid: string,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<{ success: boolean }> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.UnanetDeleteVendorTaskMapping, params: { vendorGuid, categoryGuid } });
    const headers = buildAuthHeadersForKy({ userModel, supplierModel });
    const res = await ky.delete(url, { headers });
    return (await res.json()) as { success: boolean };
};

export interface IVendorTaskMappingResponse {
    guid: string;
    vendor_id: number;
    category_id: number;
    unanet_task_key: number;
    unanet_task_name: string;
    Category: { id: number; guid: string; name: string };
}

const UnanetService = {
    testConnection,
    searchLegalEntities,
    getOrganizations,
    searchAccounts,
    searchProjects,
    getExpenseTypes,
    updateVendorMapping,
    updateVendorExpenseType,
    getAccountMappings,
    upsertAccountMapping,
    deleteAccountMapping,
    getHealth,
    getDashboardStats,
    getActivityLog,
    getQueueItemDetail,
    retryQueueItem,
    resolveQueueItem,
    getOrderSyncStatus,
    queueJournalEntry,
    processQueue,
    getSyncLogDetail,
    getProjectTasks,
    getVendorTaskMappings,
    upsertVendorTaskMapping,
    deleteVendorTaskMapping,
};

export default UnanetService;
